import { fabric } from 'fabric';
import { type CanvaSXObject, renderElement, withContext } from './canvasx';
import { Context } from '@aha-app/svgcanvas';
import type Canvas from '../../objects/Canvas';
import { calculateDimensionsForElement } from './util';

export function renderTestElement(renderer: () => CanvaSXElement): SVGElement {
  // We will render to SVG directly through fabric's Canvas, instead of our own
  // eslint-disable-next-line no-new-object
  const canvasObject = new Object(fabric.Canvas.prototype) as Canvas;

  // Context for rendered SVG
  const canvasContext = new Context({ width: 500, height: 500 });
  const canvas = (canvasContext.canvas = document.createElement('canvas'));
  canvas.style.fontFamily = 'Arial';

  // Creating a mock object for rendering, and override the render call
  const object = new fabric.Object() as CanvaSXObject & fabric.Object;

  let dimensions;
  object.render = () => {
    withContext(
      { canvas: canvasObject, ctx: canvasContext, object, scale: 1 },
      () => {
        const element = renderer();
        dimensions = calculateDimensionsForElement(element);

        renderElement(element);
      }
    );
  };

  canvasObject._renderObjects(canvasContext, [object]);

  const svg = canvasContext.getSvg();

  svg.setAttribute('width', dimensions.width);
  svg.setAttribute('height', dimensions.height);

  return svg;
}

const CHARACTER_WIDTHS = {
  '0': 0.55615234375,
  '1': 0.48564453125,
  '2': 0.55615234375,
  '3': 0.55615234375,
  '4': 0.55615234375,
  '5': 0.55615234375,
  '6': 0.55615234375,
  '7': 0.55615234375,
  '8': 0.55615234375,
  '9': 0.55615234375,
  ' ': 0.27783203125,
  '!': 0.27783203125,
  '"': 0.35498046875,
  '#': 0.55615234375,
  $: 0.55615234375,
  '%': 0.88916015625,
  '&': 0.6669921875,
  "'": 0.19091796875,
  '(': 0.3330078125,
  ')': 0.3330078125,
  '*': 0.38916015625,
  '+': 0.583984375,
  ',': 0.27783203125,
  '-': 0.3330078125,
  '.': 0.27783203125,
  '/': 0.27783203125,
  ':': 0.27783203125,
  ';': 0.27783203125,
  '<': 0.583984375,
  '=': 0.583984375,
  '>': 0.583984375,
  '?': 0.55615234375,
  '@': 1.01513671875,
  A: 0.6669921875,
  B: 0.6669921875,
  C: 0.72216796875,
  D: 0.72216796875,
  E: 0.6669921875,
  F: 0.61083984375,
  G: 0.77783203125,
  H: 0.72216796875,
  I: 0.27783203125,
  J: 0.5,
  K: 0.6669921875,
  L: 0.55615234375,
  M: 0.8330078125,
  N: 0.72216796875,
  O: 0.77783203125,
  P: 0.6669921875,
  Q: 0.77783203125,
  R: 0.72216796875,
  S: 0.6669921875,
  T: 0.61083984375,
  U: 0.72216796875,
  V: 0.6669921875,
  W: 0.94384765625,
  X: 0.6669921875,
  Y: 0.6669921875,
  Z: 0.61083984375,
  '[': 0.27783203125,
  '\\': 0.27783203125,
  ']': 0.27783203125,
  '^': 0.46923828125,
  _: 0.55615234375,
  '`': 0.3330078125,
  a: 0.55615234375,
  b: 0.55615234375,
  c: 0.5,
  d: 0.55615234375,
  e: 0.55615234375,
  f: 0.2606689453125,
  g: 0.55615234375,
  h: 0.55615234375,
  i: 0.22216796875,
  j: 0.22216796875,
  k: 0.5,
  l: 0.22216796875,
  m: 0.8330078125,
  n: 0.55615234375,
  o: 0.55615234375,
  p: 0.55615234375,
  q: 0.55615234375,
  r: 0.3330078125,
  s: 0.5,
  t: 0.27783203125,
  u: 0.55615234375,
  v: 0.5,
  w: 0.72216796875,
  x: 0.5,
  y: 0.5,
  z: 0.5,
  '{': 0.333984375,
  '|': 0.259765625,
  '}': 0.333984375,
  '~': 0.583984375,
  '…': 1,
};

/**
 * A method to get semi-accurate measurements of text for tests. The mock canvas implementation
 * does not provide results for `context.measureText`. For now we only support Arial.
 */
export function fakeMeasureText(text: string, height: number): DimensionStruct {
  const widths = Array.from(text).map(c => {
    if (!(c in CHARACTER_WIDTHS)) {
      // Just ignore/use default dimensions for other characters
      return height;
    }
    return CHARACTER_WIDTHS[c] * height;
  });

  const width = widths.reduce((sum, charWidth) => sum + charWidth, 0);

  // Approximate/average dimensions of text based on size
  return {
    width,
    height,
  };
}

/**
 * Use this function to generate the character widths for the test measureText function.
 * Run this in a browser so we can get real character widths in tests.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createCharacterWidths() {
  const size = 10;
  const length = 20;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) return;

  context.font = `${size}px Arial`;

  const chars = ['…'];
  const widths = {};
  for (let dec = 32; dec < 127; dec++) {
    chars.push(String.fromCharCode(dec));
  }

  chars.forEach(char => {
    const dimensions = context?.measureText(Array(length).fill(char).join(''));

    widths[char] = dimensions.width / length / size;
  });

  console.log(JSON.stringify(widths));
}
